// Admin settings: shipping defaults plus the shop's payment methods.
//
// Payment methods are configured once here; each invoice then selects which
// of them it accepts (see SendInvoiceModal). The `details` field is what the
// customer actually needs — a Zelle address, a Venmo handle — so it's shown
// verbatim on the invoice and in the invoice email.
import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, Plus, Trash2, X } from 'lucide-react';
import {
  adminGetSettings,
  adminSavePaymentMethods,
  adminUpdateSetting,
  describeApiError,
  getPaymentMethods,
  uploadImage,
} from '../../services/api';
import type { PaymentMethodOption } from '../../types';
import { STRIPE_METHOD_KEY } from '../../types';

const SETTING_KEYS: { key: string; label: string; hint?: string }[] = [
  {
    key: 'tax_rate_percent',
    label: 'Default tax rate (%)',
    hint: 'Applied to new orders. Can be overridden per order. Blank = no tax.',
  },
  { key: 'business_name', label: 'Business name (invoice footer)' },
  { key: 'business_contact_name', label: 'Contact name (invoice footer)' },
  { key: 'business_email', label: 'Contact email (invoice footer)' },
  {
    key: 'business_social',
    label: 'Social handle (invoice footer)',
    hint: 'Shown next to the Instagram and TikTok marks, e.g. @bleumscreations. Both marks link to this handle unless you override one below.',
  },
  {
    key: 'instagram_handle',
    label: 'Instagram handle (optional)',
    hint: 'Only needed if it differs from the handle above. A full profile URL works too.',
  },
  {
    key: 'tiktok_handle',
    label: 'TikTok handle (optional)',
    hint: 'Only needed if it differs from the handle above. A full profile URL works too.',
  },
  { key: 'shipping_flat_rate_cents', label: 'Flat shipping rate (cents)' },
  { key: 'free_shipping_threshold_cents', label: 'Free shipping threshold (cents)' },
  {
    key: 'pickup_address',
    label: 'Pickup / drop-off address',
    hint: 'Printed on the invoice for pickup orders, so the customer knows where to come.',
  },
];

// Settings that are on/off rather than free text. Empty for now: the
// receipt toggle that lived here switched off an automatic email that no
// longer exists — nothing is sent unless it's sent from an order.

const slugify = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32);

const Settings: React.FC = () => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [methods, setMethods] = useState<PaymentMethodOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | undefined>();
  const [savingMethods, setSavingMethods] = useState(false);
  const [methodsSaved, setMethodsSaved] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState<number | undefined>();
  const [error, setError] = useState<string | undefined>();
  // One hidden file input per method row, so each logo button opens its own.
  const logoInputs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    Promise.all([adminGetSettings(), getPaymentMethods()])
      .then(([data, paymentMethods]: [Record<string, any>, PaymentMethodOption[]]) => {
        const flat: Record<string, string> = {};
        for (const k of Object.keys(data ?? {})) {
          // payment_methods has its own editor below; don't stringify it into
          // a text input.
          if (k === 'payment_methods') continue;
          flat[k] = String(data[k] ?? '');
        }
        setValues(flat);
        setMethods(paymentMethods);
      })
      .catch(e => setError(describeApiError(e, 'Failed to load settings')))
      .finally(() => setLoading(false));
  }, []);

  const saveValue = async (key: string, value: string) => {
    setSavingKey(key);
    setError(undefined);
    try {
      await adminUpdateSetting(key, value);
    } catch (e: any) {
      setError(describeApiError(e, 'Save failed'));
    } finally {
      setSavingKey(undefined);
    }
  };

  // Text fields save on their own button; toggles save on click.
  const save = (key: string) => saveValue(key, values[key]);

  const updateMethod = (index: number, patch: Partial<PaymentMethodOption>) => {
    setMethods(prev => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
    setMethodsSaved(false);
  };

  const addMethod = () => {
    setMethods(prev => [...prev, { key: '', label: '', details: '', instructions: '' }]);
    setMethodsSaved(false);
  };

  const removeMethod = (index: number) => {
    setMethods(prev => prev.filter((_, i) => i !== index));
    setMethodsSaved(false);
  };

  /**
   * Attach a logo to one method.
   *
   * Uploaded like any other image, so it lands in the same bucket and is
   * served from the CDN — the invoice is viewed by customers and printed,
   * and both need a URL that resolves without signing in.
   */
  const pickLogo = async (index: number, files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setUploadingLogo(index);
    setError(undefined);
    try {
      const { s3_url } = await uploadImage(file, 'product');
      updateMethod(index, { logo_url: s3_url });
    } catch (e: any) {
      setError(describeApiError(e, 'Logo upload failed'));
    } finally {
      setUploadingLogo(undefined);
      const input = logoInputs.current[index];
      if (input) input.value = '';
    }
  };

  const saveMethods = async () => {
    setSavingMethods(true);
    setError(undefined);
    try {
      // Derive a key from the label when the admin didn't set one, and drop
      // rows that are still blank.
      const cleaned = methods
        .map(m => ({ ...m, key: m.key || slugify(m.label) }))
        .filter(m => m.key && m.label);
      const duplicate = cleaned.find((m, i) => cleaned.findIndex(o => o.key === m.key) !== i);
      if (duplicate) {
        setError(`Duplicate payment method key: ${duplicate.key}`);
        return;
      }
      await adminSavePaymentMethods(cleaned);
      setMethods(cleaned);
      setMethodsSaved(true);
    } catch (e: any) {
      setError(describeApiError(e, 'Save failed'));
    } finally {
      setSavingMethods(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Settings</h1>
      {loading && <div className="text-text-light">Loading…</div>}
      {error && <div className="text-red-600 text-sm mb-4">{error}</div>}

      {!loading && (
        <div className="space-y-10 max-w-2xl">
          <section className="space-y-4">
            <h2 className="text-lg font-medium">Shipping</h2>
            {SETTING_KEYS.map(({ key, label, hint }) => (
              <div key={key} className="space-y-1">
                <label className="block text-sm font-medium">{label}</label>
                {hint && <p className="text-xs text-text-light">{hint}</p>}
                <div className="flex gap-2">
                  <input
                    value={values[key] ?? ''}
                    onChange={e => setValues({ ...values, [key]: e.target.value })}
                    className="flex-1 border border-background-dark rounded px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => save(key)}
                    disabled={savingKey === key}
                    className="bg-primary text-white rounded px-3 py-2 text-sm font-medium disabled:opacity-50"
                  >
                    {savingKey === key ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            ))}
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-medium">Customer emails</h2>
            <p className="text-sm text-text-light">
              Nothing is emailed automatically. Saving an order, changing its
              status and marking it paid are all silent — a customer only
              hears from you when you open their order and press{' '}
              <strong className="text-text">Email invoice</strong> or{' '}
              <strong className="text-text">Send email</strong>.
            </p>
          </section>

          <section className="space-y-3">
            <div>
              <h2 className="text-lg font-medium">Payment methods</h2>
              <p className="text-xs text-text-light">
                What you can accept. Each invoice picks from this list — the details
                you enter here are shown to the customer, so put the exact Zelle
                address or Venmo handle they should send to.
              </p>
            </div>

            <div className="space-y-3">
              {methods.map((m, i) => {
                const isCard = m.key === STRIPE_METHOD_KEY;
                return (
                  <div
                    key={i}
                    className="border border-background-dark rounded p-3 space-y-2 bg-white"
                  >
                    <div className="flex gap-2 items-start">
                      <input
                        value={m.emoji ?? ''}
                        onChange={e => updateMethod(i, { emoji: e.target.value })}
                        placeholder="🙂"
                        aria-label={`Emoji for ${m.label || 'method'}`}
                        title="Used when no logo is uploaded, and as the logo's alt text"
                        className="w-16 text-center border border-background-dark rounded px-2 py-2 text-sm"
                      />
                      <input
                        value={m.label}
                        onChange={e => updateMethod(i, { label: e.target.value })}
                        placeholder="Label (e.g. Zelle)"
                        className="flex-1 border border-background-dark rounded px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeMethod(i)}
                        className="text-red-600 hover:bg-red-50 rounded p-2"
                        aria-label={`Remove ${m.label || 'method'}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    {/* Logo, optional per method — whatever is missing one
                        falls back to its emoji, so a shop can mix the two. */}
                    <div className="flex items-center gap-2">
                      <input
                        ref={el => {
                          logoInputs.current[i] = el;
                        }}
                        type="file"
                        accept="image/*"
                        onChange={e => pickLogo(i, e.target.files)}
                        className="hidden"
                      />
                      <div className="h-9 w-9 shrink-0 rounded border border-background-dark bg-background flex items-center justify-center overflow-hidden">
                        {m.logo_url ? (
                          <img
                            src={m.logo_url}
                            alt=""
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <span className="text-base">{m.emoji || '—'}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => logoInputs.current[i]?.click()}
                        disabled={uploadingLogo === i}
                        className="inline-flex items-center gap-1 border border-background-dark rounded px-3 py-2 text-sm hover:border-primary disabled:opacity-50"
                      >
                        <ImagePlus size={14} />
                        {uploadingLogo === i
                          ? 'Uploading…'
                          : m.logo_url
                            ? 'Replace logo'
                            : 'Upload logo'}
                      </button>
                      {m.logo_url && (
                        <button
                          type="button"
                          onClick={() => updateMethod(i, { logo_url: '' })}
                          className="inline-flex items-center gap-1 text-text-light hover:text-red-600 rounded px-2 py-2 text-sm"
                        >
                          <X size={14} /> Use emoji
                        </button>
                      )}
                    </div>

                    {isCard ? (
                      <p className="text-xs text-text-light">
                        Card payments run through Stripe. Selecting this on an invoice
                        creates a payment link automatically — no details needed here.
                      </p>
                    ) : (
                      <input
                        value={m.details ?? ''}
                        onChange={e => updateMethod(i, { details: e.target.value })}
                        placeholder="Details the customer pays to (e.g. bleums@example.com, @bleums-creations)"
                        className="w-full border border-background-dark rounded px-3 py-2 text-sm"
                      />
                    )}

                    <input
                      value={m.instructions ?? ''}
                      onChange={e => updateMethod(i, { instructions: e.target.value })}
                      placeholder="Extra instructions (optional)"
                      className="w-full border border-background-dark rounded px-3 py-2 text-sm"
                    />
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={addMethod}
                className="inline-flex items-center gap-1 border border-background-dark rounded px-3 py-2 text-sm hover:border-primary"
              >
                <Plus size={14} /> Add method
              </button>
              <button
                type="button"
                onClick={saveMethods}
                disabled={savingMethods}
                className="bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {savingMethods ? 'Saving…' : 'Save payment methods'}
              </button>
              {methodsSaved && <span className="text-sm text-green-700">Saved</span>}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default Settings;
